import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useState,
} from 'react';
import { TopicDiff } from './types';

/**
 * Lightweight React context holding the diff responses fetched by this
 * mount's `WhatsChangedPanel`. The provider wraps the topic-page area so
 * one page-load = one cache lifetime; collapse / re-expand cycles reuse
 * the cached response, and navigating to a different topic unmounts the
 * provider and discards the cache (Requirement 3.6).
 *
 * Cache key: `${documentId}:${lcmsXmlGUID}:${toVersionId}`.
 */

type Cache = Record<string, TopicDiff>;

type ContextValue = {
    getCached: (cacheKey: string) => TopicDiff | undefined;
    setCached: (cacheKey: string, diff: TopicDiff) => void;
};

const WhatsChangedPanelContext = createContext<ContextValue | undefined>(
    undefined
);

/**
 * Stable no-op accessors returned by {@link useWhatsChangedPanelCache}
 * when the component is rendered outside a provider. Defined at module
 * scope so that `getCached` / `setCached` keep the same identity across
 * every render: WhatsChangedPanel uses `getCached` as a useEffect
 * dependency, and a fresh object each render would re-run the effect
 * and clobber the just-fetched diff.
 */
const NOOP_CACHE: ContextValue = {
    getCached: () => undefined,
    setCached: () => undefined,
};

export const buildCacheKey = (
    documentId: string,
    lcmsXmlGUID: string,
    toVersionId: string
) => `${documentId}:${lcmsXmlGUID}:${toVersionId}`;

export const WhatsChangedPanelProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [cache, setCache] = useState<Cache>({});

    const getCached = useCallback(
        (cacheKey: string) => cache[cacheKey],
        [cache]
    );
    const setCached = useCallback((cacheKey: string, diff: TopicDiff) => {
        setCache((prev) => ({ ...prev, [cacheKey]: diff }));
    }, []);

    return (
        <WhatsChangedPanelContext.Provider value={{ getCached, setCached }}>
            {children}
        </WhatsChangedPanelContext.Provider>
    );
};

/**
 * Returns the cache accessors. Returns a no-op implementation when the
 * component is rendered outside a provider so standalone use (and tests)
 * degrade gracefully — expand will refetch, but the component still works.
 */
export const useWhatsChangedPanelCache = (): ContextValue => {
    const ctx = useContext(WhatsChangedPanelContext);
    return ctx ?? NOOP_CACHE;
};
